/**
 * Helpers for presenting a Communication resource that is the source of an
 * extracted clinical resource. When an extraction is derived from a note
 * (Communication) rather than an uploaded document (DocumentReference), the
 * review experience shows the note's name, date, and body in place of the
 * document preview.
 */

const FOUNTAIN_VISIT_NOTE_SYSTEM =
  'http://lifeomic.com/fhir/fountain-visit-note';

// Mirrors the note-type labels used on the member Notes tab so the same note
// reads consistently wherever it is surfaced.
const FOUNTAIN_VISIT_NOTE_CODE_LABELS: Record<string, string> = {
  nonvisit: 'Non-Visit Note',
  visit: 'Visit Note',
  email: 'Email Note',
};

/**
 * Resolve a human-readable name for the source note, preferring the Fountain
 * visit-note type tag, then the Communication's category/reason text. Returns
 * `null` when nothing usable is present so the caller can apply a fallback.
 */
export const getCommunicationSourceTitle = (
  communication: fhir.Communication | null,
): string | null => {
  if (!communication) {
    return null;
  }

  const tagCode = communication.meta?.tag?.find(
    tag => tag.system === FOUNTAIN_VISIT_NOTE_SYSTEM,
  )?.code;
  if (tagCode) {
    return FOUNTAIN_VISIT_NOTE_CODE_LABELS[tagCode] ?? tagCode;
  }

  return (
    communication.category?.[0]?.text ||
    communication.reasonCode?.[0]?.text ||
    null
  );
};

/**
 * The raw FHIR date string for when the note was created, preferring `sent`
 * and falling back to `meta.lastUpdated`. Formatting is left to the caller.
 */
export const getCommunicationSourceDate = (
  communication: fhir.Communication | null,
): string | null =>
  communication?.sent || communication?.meta?.lastUpdated || null;

/**
 * The note body, read from the first payload `contentString` and falling back
 * to the first `note.text`.
 */
export const getCommunicationSourceContent = (
  communication: fhir.Communication | null,
): string =>
  (communication?.payload?.[0] as { contentString?: string } | undefined)
    ?.contentString ||
  communication?.note?.[0]?.text ||
  '';
